import { useTranslation } from "react-i18next";
import type { ClientsheetOverviewPublic, ClientsheetOverviewsPublic } from "@/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function CardStatsSection({ data }: Pick<ClientsheetOverviewsPublic, "data">) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;

  const sortedData = [...data].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const latest = sortedData[0];

  const previous = sortedData.length > 1 ? sortedData[1] : null;

  const getChangePercentage = (current: number, previousValue: number): number | null => {
    if (previousValue === 0) return null; // Avoid division by zero
    return ((current - previousValue) / previousValue) * 100;
  };

  const formatInfoText = (percentageChange: number | null, previousDate: string | null): string | null => {
    if (percentageChange === null || previousDate === null) return null;
    return t("percentageChangeFromDate", {
      sign: percentageChange > 0 ? "+" : "",
      percentage: percentageChange.toFixed(1),
      date: previousDate,
    });
  };

  const generateStatInfo = (
    currentAmount: number,
    previousRecord: ClientsheetOverviewPublic | null,
    previousAmountKey: keyof ClientsheetOverviewPublic,
  ) => {
    if (!previousRecord) return null;
    const previousAmount = previousRecord[previousAmountKey] as number;
    const percentageChange = getChangePercentage(currentAmount, previousAmount);
    return formatInfoText(percentageChange, previousRecord.date);
  };

  const stats = [
    {
      title: t("Margin Amount"),
      amount: latest.margin_amount,
      info: generateStatInfo(latest.margin_amount, previous, "margin_amount"),
    },
    {
      title: t("Delivered Amount"),
      amount: latest.delivered_amount,
      info: generateStatInfo(latest.delivered_amount, previous, "delivered_amount"),
    },
    {
      title: t("Invoiced Amount"),
      amount: latest.invoiced_amount,
      info: generateStatInfo(latest.invoiced_amount, previous, "invoiced_amount"),
    },
  ] satisfies { title: string; amount: number; info: string | null }[];

  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 md:gap-4">
      {stats.map((stat) => (
        <CardStat key={stat.title} title={stat.title} amount={stat.amount} info={stat.info} />
      ))}
    </section>
  );
}

function CardStat({ title, amount, info }: { title: string; amount: number; info: string | null }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-normal">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-bold sm:text-2xl">
          {amount.toLocaleString("nl-NL", {
            style: "currency",
            currency: "EUR",
          })}
        </div>
        {info && <p className="text-xs text-muted-foreground">{info}</p>}
      </CardContent>
    </Card>
  );
}
